import React, { useState, useEffect, useRef } from 'react'
import '../styles/Products.css'

const BASE_URL = 'https://api.admin.yourhood.ge'

function Products() {
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const PAGE_SIZE = 16

  const [searchText, setSearchText] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [modal, setModal] = useState(null)
  const [selected, setSelected] = useState(null)
  const [formTitle, setFormTitle] = useState('')
  const [formDesc, setFormDesc] = useState('')
  const [formPrice, setFormPrice] = useState('')
  const [formSalePrice, setFormSalePrice] = useState('')
  const [formCategory, setFormCategory] = useState('')
  const [formSelectedSizes, setFormSelectedSizes] = useState([])
  const [formColors, setFormColors] = useState('')
  const [uploadingImages, setUploadingImages] = useState(false)
  const [loadingImages, setLoadingImages] = useState(false)

  // ── unified image list: { id, url, file?, isNew }
  // order in this array = sortOrder sent to API
  const [imageSlots, setImageSlots] = useState([])

  const imgInputRef = useRef()

  const token = localStorage.getItem('token')
  const authHeaders = { 'Content-Type': 'application/json', 'X-Language': 'ka', 'Authorization': token }
  const availableSizes = [...new Set(categories.find(c => c.id === formCategory)?.sizes || [])]

  const toggleSize = (size) => {
    setFormSelectedSizes(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size])
  }

  const fetchProducts = async (p = 1, reset = false) => {
    setIsLoading(true); setError('')
    try {
      let url = `${BASE_URL}/api/Product/protudcts?Page=${p}&PageSize=${PAGE_SIZE}`
      if (filterCategory) url += `&CategoryID=${filterCategory}`
      if (searchText) url += `&SearchText=${encodeURIComponent(searchText)}`
      const res = await fetch(url, { headers: { 'X-Language': 'ka', 'Authorization': token } })
      const data = await res.json()
      if (res.ok) {
        const list = Array.isArray(data) ? data : []
        setProducts(reset ? list : prev => [...prev, ...list])
        setHasMore(list.length === PAGE_SIZE)
      } else { setError('პროდუქტების ჩატვირთვა ვერ მოხერხდა') }
    } catch { setError('კავშირის შეცდომა') }
    finally { setIsLoading(false) }
  }

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/Categories/categories`, { headers: { 'X-Language': 'ka', 'Authorization': token } })
      if (res.ok) setCategories(await res.json())
    } catch {}
  }

  useEffect(() => { fetchCategories() }, [])
  useEffect(() => { setPage(1); fetchProducts(1, true) }, [filterCategory, searchText])

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000) }

  const closeModal = () => {
    setModal(null); setSelected(null)
    setFormTitle(''); setFormDesc(''); setFormPrice('')
    setFormSalePrice(''); setFormCategory(''); setFormSelectedSizes([])
    setFormColors(''); setImageSlots([]); setError('')
  }

  const openAdd = () => {
    setFormCategory(categories[0]?.id || '')
    setFormSelectedSizes([])
    setFormColors('')
    setImageSlots([])
    setModal('add')
  }

  const openEdit = async (p) => {
    setSelected(p)
    setFormTitle(p.title || '')
    setFormDesc('')              // ← clear until detail loads
    setFormPrice(p.price?.toString() || '')
    setFormSalePrice(p.salePrice?.toString() || '')
    setFormCategory(p.categoryID || '')
    setFormSelectedSizes(p.availableSizes || [])
    setFormColors((p.colors || []).join(', '))
    setImageSlots([])
    setLoadingImages(true)
    setModal('edit')

    try {
      const res = await fetch(`${BASE_URL}/api/Product/protudct-details?ProductID=${p.id}`, {
        headers: { 'X-Language': 'ka', 'Authorization': token }
      })
      const source = res.ok ? await res.json() : p
      setFormDesc(source.description || '')   // ← now set from detail response
      const slots = (source.imageIDs || []).map((id, i) => ({
        id,
        url: source.imageUrls?.[i] || '',
        isNew: false,
      }))
      setImageSlots(slots)
    } catch {
      setFormDesc(p.description || '')
      const fallback = (p.imageIDs || []).map((id, i) => ({ id, url: p.imageUrls?.[i] || '', isNew: false }))
      setImageSlots(fallback)
    } finally {
      setLoadingImages(false)
    }
  }

  const openDelete = (p) => { setSelected(p); setModal('delete') }

  // ── add new files to end of slots
  const handleImgChange = (e) => {
    const files = Array.from(e.target.files)
    const newSlots = files.map(file => ({
      id: null,
      url: URL.createObjectURL(file),
      file,
      isNew: true,
    }))
    setImageSlots(prev => [...prev, ...newSlots])
    e.target.value = ''
  }

  const removeSlot = (i) => setImageSlots(prev => prev.filter((_, idx) => idx !== i))

  // drag-to-reorder
  const dragIdx = useRef(null)
  const onDragStart = (i) => { dragIdx.current = i }
  const onDragOver = (e, i) => {
    e.preventDefault()
    if (dragIdx.current === null || dragIdx.current === i) return
    setImageSlots(prev => {
      const next = [...prev]
      const [moved] = next.splice(dragIdx.current, 1)
      next.splice(i, 0, moved)
      dragIdx.current = i
      return next
    })
  }
  const onDragEnd = () => { dragIdx.current = null }

  // upload only new images, return { localUrl -> uploadedId }
  const uploadNewImages = async () => {
    const newSlots = imageSlots.filter(s => s.isNew && s.file)
    if (!newSlots.length) return {}
    setUploadingImages(true)
    const formData = new FormData()
    newSlots.forEach(s => formData.append('images', s.file))
    formData.append('imageType', '0')
    try {
      const res = await fetch(`${BASE_URL}/api/ImageUpload/upload`, {
        method: 'PUT',
        headers: { 'X-Language': 'ka', 'Authorization': token },
        body: formData,
      })
      if (res.ok) {
        const data = await res.json()
        const ids = Array.isArray(data) ? data.map(d => d.id || d) : []
        // map by index — same order we uploaded
        const map = {}
        newSlots.forEach((s, i) => { map[s.url] = ids[i] })
        return map
      }
      return {}
    } catch { return {} }
    finally { setUploadingImages(false) }
  }

  // build images array with correct sortOrder for API
  const buildImagesPayload = async () => {
    const urlToId = await uploadNewImages()
    return imageSlots.map((slot, i) => ({
      sortOrder: i,
      imageId: slot.isNew ? (urlToId[slot.url] || null) : slot.id,
    })).filter(s => s.imageId)
  }

  const parseColors = () => formColors.split(',').map(s => s.trim()).filter(Boolean)

  const handleAdd = async () => {
    if (!formTitle.trim()) { setError('გთხოვთ შეიყვანოთ სათაური'); return }
    if (!formCategory)     { setError('გთხოვთ აირჩიოთ კატეგორია'); return }
    if (!formPrice)        { setError('გთხოვთ შეიყვანოთ ფასი'); return }
    setIsLoading(true)
    try {
      const images = await buildImagesPayload()
      const body = {
        categoryid: formCategory,
        images,
        avaibleSizes: formSelectedSizes,
        colors: parseColors(),
        price: parseFloat(formPrice),
        salePrice: formSalePrice ? parseFloat(formSalePrice) : null,
        translations: [{ languageCode: 'ka', title: formTitle, description: formDesc }]
      }
      const res = await fetch(`${BASE_URL}/api/Product/add-product`, { method: 'PUT', headers: authHeaders, body: JSON.stringify(body) })
      const data = await res.json()
      if (res.ok && data === true) { closeModal(); showSuccess('პროდუქტი წარმატებით დაემატა'); fetchProducts(1, true); setPage(1) }
      else { setError('დამატება ვერ მოხერხდა') }
    } catch { setError('კავშირის შეცდომა') }
    finally { setIsLoading(false) }
  }

  const handleEdit = async () => {
    if (!formTitle.trim()) { setError('გთხოვთ შეიყვანოთ სათაური'); return }
    if (!formPrice)        { setError('გთხოვთ შეიყვანოთ ფასი'); return }
    setIsLoading(true)
    try {
      const images = await buildImagesPayload()
      const body = {
        productID: selected.id,
        categoryID: formCategory,
        images,
        avaibleSizes: formSelectedSizes,
        colors: parseColors(),
        price: parseFloat(formPrice),
        salePrice: formSalePrice ? parseFloat(formSalePrice) : null,
        translations: [{ languageCode: 'ka', title: formTitle, description: formDesc }]
      }
      const res = await fetch(`${BASE_URL}/api/Product/edit-product`, { method: 'PUT', headers: authHeaders, body: JSON.stringify(body) })
      const data = await res.json()
      if (res.ok && data === true) { closeModal(); showSuccess('პროდუქტი წარმატებით განახლდა'); fetchProducts(1, true); setPage(1) }
      else { setError('განახლება ვერ მოხერხდა') }
    } catch { setError('კავშირის შეცდომა') }
    finally { setIsLoading(false) }
  }

  const handleDelete = async () => {
    setIsLoading(true)
    try {
      const res = await fetch(`${BASE_URL}/api/Product/delete-product`, { method: 'DELETE', headers: authHeaders, body: JSON.stringify({ productid: selected.id }) })
      if (res.ok) { closeModal(); showSuccess('პროდუქტი წარმატებით წაიშალა'); fetchProducts(1, true); setPage(1) }
      else { setError('წაშლა ვერ მოხერხდა') }
    } catch { setError('კავშირის შეცდომა') }
    finally { setIsLoading(false) }
  }

  const loadMore = () => { const next = page + 1; setPage(next); fetchProducts(next, false) }
  const isSaving = isLoading || uploadingImages || loadingImages

  return (
    <div className="pr-wrapper">
      <div className="pr-header">
        <div>
          <h2 className="pr-title">პროდუქტები</h2>
          <p className="pr-subtitle">{products.length} ჩანაწერი</p>
        </div>
        <button className="pr-btn-add" onClick={openAdd}>+ დამატება</button>
      </div>

      <div className="pr-filters">
        <input className="pr-search" placeholder="ძიება..." value={searchText} onChange={e => setSearchText(e.target.value)} />
        <select className="pr-select-filter" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
          <option value="">ყველა კატეგორია</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
      </div>

      {success && <div className="pr-success">{success}</div>}
      {error && !modal && <div className="pr-error">{error}</div>}

      {isLoading && products.length === 0 ? (
        <div className="pr-loading">იტვირთება...</div>
      ) : products.length === 0 ? (
        <div className="pr-empty">პროდუქტები არ მოიძებნა</div>
      ) : (
        <>
          <div className="pr-grid">
            {products.map(p => (
              <div key={p.id} className="pr-card">
                <div className="pr-card-img-wrap">
                  {p.imageUrls?.[0]
                    ? <img src={p.imageUrls[0]} alt={p.title} className="pr-card-img" />
                    : <div className="pr-card-no-img">ფოტო არ არის</div>}
                </div>
                <div className="pr-card-body">
                  <p className="pr-card-category">{p.categoryName}</p>
                  <p className="pr-card-name">{p.title || '—'}</p>
                  <p className="pr-card-price">{p.price} ₾</p>
                  <div className="pr-card-sizes">{p.availableSizes?.map((s, i) => <span key={i} className="pr-size-tag">{s}</span>)}</div>
                </div>
                <div className="pr-card-actions">
                  <button className="pr-btn-edit" onClick={() => openEdit(p)}>რედაქტირება</button>
                  <button className="pr-btn-delete" onClick={() => openDelete(p)}>წაშლა</button>
                </div>
              </div>
            ))}
          </div>
          {hasMore && (
            <div className="pr-loadmore">
              <button className="pr-btn-loadmore" onClick={loadMore} disabled={isLoading}>
                {isLoading ? 'იტვირთება...' : 'მეტის ჩვენება'}
              </button>
            </div>
          )}
        </>
      )}

      {modal && (
        <div className="pr-overlay" onClick={closeModal}>
          <div className="pr-modal" onClick={e => e.stopPropagation()}>
            {modal === 'delete' ? (
              <>
                <h3 className="pr-modal-title">პროდუქტის წაშლა</h3>
                <p className="pr-modal-text">დარწმუნებული ხართ, რომ გსურთ <strong>{selected?.title || 'ეს პროდუქტი'}</strong> წაშლა?</p>
                {error && <div className="pr-error">{error}</div>}
                <div className="pr-modal-actions">
                  <button className="pr-btn-ghost" onClick={closeModal} disabled={isSaving}>გაუქმება</button>
                  <button className="pr-btn-danger" onClick={handleDelete} disabled={isSaving}>{isSaving ? 'წაშლა...' : 'წაშლა'}</button>
                </div>
              </>
            ) : (
              <>
                <h3 className="pr-modal-title">{modal === 'add' ? 'ახალი პროდუქტი' : 'პროდუქტის რედაქტირება'}</h3>

                <div className="pr-form-group">
                  <label className="pr-label">სათაური</label>
                  <input className="pr-input" placeholder="პროდუქტის სახელი" value={formTitle} onChange={e => setFormTitle(e.target.value)} disabled={isSaving} />
                </div>

                <div className="pr-form-group">
                  <label className="pr-label">აღწერა</label>
                  <textarea className="pr-textarea" placeholder="პროდუქტის აღწერა" value={formDesc} onChange={e => setFormDesc(e.target.value)} disabled={isSaving} rows={3} />
                </div>

                <div className="pr-form-row">
                  <div className="pr-form-group">
                    <label className="pr-label">ფასი (₾)</label>
                    <input className="pr-input" type="number" placeholder="0.00" value={formPrice} onChange={e => setFormPrice(e.target.value)} disabled={isSaving} min="0" step="0.01" />
                  </div>
                  <div className="pr-form-group">
                    <label className="pr-label">ფასდაკლება (₾)</label>
                    <input className="pr-input" type="number" placeholder="0.00" value={formSalePrice} onChange={e => setFormSalePrice(e.target.value)} disabled={isSaving} min="0" step="0.01" />
                  </div>
                </div>

                <div className="pr-form-group">
                  <label className="pr-label">კატეგორია</label>
                  <select className="pr-select" value={formCategory} onChange={e => { setFormCategory(e.target.value); setFormSelectedSizes([]) }} disabled={isSaving}>
                    <option value="">აირჩიეთ</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                </div>

                {availableSizes.length > 0 && (
                  <div className="pr-form-group">
                    <label className="pr-label">ზომები</label>
                    <div className="pr-size-picker">
                      {availableSizes.map(size => (
                        <button key={size} type="button"
                          className={`pr-size-chip ${formSelectedSizes.includes(size) ? 'pr-size-chip--active' : ''}`}
                          onClick={() => toggleSize(size)} disabled={isSaving}>{size}</button>
                      ))}
                    </div>
                    {formSelectedSizes.length > 0 && <p className="pr-size-selected-hint">არჩეული: {formSelectedSizes.join(', ')}</p>}
                  </div>
                )}

                <div className="pr-form-group">
                  <label className="pr-label">ფერები (მძიმით გამოყოფილი)</label>
                  <input className="pr-input" placeholder="red, blue, green" value={formColors} onChange={e => setFormColors(e.target.value)} disabled={isSaving} />
                </div>

                <div className="pr-form-group">
                  <label className="pr-label">
                    ფოტოები
                    <span className="pr-label-hint"> — გადაათრიეთ თანმიმდევრობის შესაცვლელად</span>
                  </label>
                  {loadingImages ? (
                    <div className="pr-imgs-loading">ფოტოები იტვირთება...</div>
                  ) : (
                    <div className="pr-img-grid">
                      {imageSlots.map((slot, i) => (
                        <div
                          key={slot.isNew ? `new-${slot.url}` : slot.id}
                          className={`pr-img-thumb${slot.isNew ? ' pr-img-thumb--new' : ''}`}
                          draggable
                          onDragStart={() => onDragStart(i)}
                          onDragOver={e => onDragOver(e, i)}
                          onDragEnd={onDragEnd}
                        >
                          <img src={slot.url} alt="" />
                          <span className="pr-img-order">{i + 1}</span>
                          <button className="pr-img-remove" onClick={() => removeSlot(i)}>×</button>
                        </div>
                      ))}
                      <div className="pr-img-add" onClick={() => imgInputRef.current?.click()}>
                        <span>+</span>
                      </div>
                      <input ref={imgInputRef} type="file" accept="image/*" multiple className="pr-input-hidden" onChange={handleImgChange} />
                    </div>
                  )}
                </div>

                {error && <div className="pr-error">{error}</div>}

                <div className="pr-modal-actions">
                  <button className="pr-btn-ghost" onClick={closeModal} disabled={isSaving}>გაუქმება</button>
                  <button className="pr-btn-primary" onClick={modal === 'add' ? handleAdd : handleEdit} disabled={isSaving}>
                    {isSaving ? 'შენახვა...' : 'შენახვა'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Products